import {
  BookmarkSimple,
  DotsThreeVertical,
  Flag,
  Heart,
  MapPin,
  PaperPlaneTilt,
  Trash,
} from '@phosphor-icons/react';
import type { Comment, Post, PostMention } from '@sirocco/shared';
import { useEffect, useRef, useState } from 'react';
import { Link, useLocation, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { posts as postsApi } from '../../api/endpoints';
import { ScreenError, ScreenLoader, SubScreen } from '../../components/layout/Screens';
import { Avatar } from '../../components/ui/Avatar';
import { EmptyState } from '../../components/ui/Bits';
import { PhotoViewer } from '../../components/ui/PhotoViewer';
import { ConfirmDialog, useConfirm } from '../../components/ui/Confirm';
import { PartageSheet } from '../../components/ui/PartageSheet';
import { Sheet, SheetAction } from '../../components/ui/Sheet';
import { reportPath } from '../report/ReportScreen';
import { useFlashOnArrival } from '../../utils/flash';
import { lienDePost } from '../../utils/partage';
import { readScreenCache, useAsync } from '../../hooks/useAsync';
import { propagerLePost } from '../../utils/postEnCache';
import { horodatage } from '../../utils/format';
import { AttachedEvent } from '../../components/PostCard';
import { PostText } from '../../components/ui/PostText';
import { CommentComposer, type PoigneeComposeur } from './CommentComposer';
import { CommentRow } from './CommentRow';
import { useSession } from '../../store/SessionContext';
import { profilePath } from '../../utils/routes';
import { t } from '../../i18n';

/**
 * Les reponses a montrer sous un post.
 *
 * Celles posees sous le post, et celles dont le parent n'existe plus.
 *
 * Effacer une reponse n'efface pas ce qui lui repondait : ces reponses
 * gardent le nom d'un parent disparu, et le filtre sur « aucun parent »
 * les faisait alors sortir de partout — plus dans le fil du post, ni dans
 * aucun autre, alors que leur texte existe toujours en base. Elles
 * remontent ici, ce qui est aussi ce que le compteur du post compte.
 */
function racines(commentaires: Comment[]): Comment[] {
  const connus = new Set(commentaires.map((commentaire) => commentaire.id));
  return commentaires.filter(
    (commentaire) => commentaire.parentId === null || !connus.has(commentaire.parentId),
  );
}

/**
 * Les commentaires d'un post.
 *
 * Rien n'est encarte ici : le post et les reponses posent directement sur
 * le fond de l'ecran, separes par de simples filets. C'est une
 * conversation, pas une liste de cartes — et le post en tete se lit comme
 * dans le feed, avec ses propres actions.
 *
 * L'ecran precedent passe le post dans l'etat de navigation : il l'a deja
 * sous la main, et le rappeler en tete ne coute alors rien. Arrive
 * autrement — la cloche du fil, un lien colle — l'ecran va le chercher
 * lui-meme par `postIndex`, plutot que d'afficher des reponses sans ce a
 * quoi elles repondent.
 */
export function CommentsScreen() {
  const { postId = '' } = useParams();
  const { state } = useLocation();
  const navigate = useNavigate();
  const { user } = useSession();

  /*
    Le post vit dans l'ecran : liker ou mettre en signet le met a jour ici.

    Il vient d'abord de la navigation, puis de la memoire d'ecran, et du
    reseau en dernier recours. La memoire compte pour un cas precis : un
    ecran remonte sans etat de navigation. C'est ce qui arrive a celui
    qu'on entrevoit derriere un geste de retour — il redemandait alors au
    reseau, en trois vagues, un post affiche une seconde plus tot.
  */
  const [post, setPost] = useState<Post | null>(
    (state as { post?: Post } | null)?.post ?? readScreenCache<Post>(`post:${postId}`) ?? null,
  );
  const [viewed, setViewed] = useState<string | null>(null);

  const { data, loading, error, reload, setData } = useAsync(
    () => postsApi.comments(postId),
    [postId],
    `comments:${postId}`,
  );

  /*
    Le post, quand il n'est pas venu avec la navigation.

    Seulement s'il manque : l'ecran d'ou l'on vient l'a presque toujours
    passe, et cette lecture ne sert qu'aux entrees de cote — la cloche,
    une notification touchee hors de l'application. Celle-ci tombe
    parfois pendant que l'application se reveille : une lecture ratee
    laissait alors les reponses seules, sans le post au-dessus. On
    reessaie deux fois avant de renoncer.
  */
  const [postCherche, setPostCherche] = useState(false);
  useEffect(() => {
    if (post) {
      /*
        Ce qu'on affiche, on le pose partout ou ce post est deja en
        memoire — pas seulement dans sa propre fiche. Le fil ne se relit
        pas tout seul en revenant : sans cela, un like mis ici y
        reapparaissait vide, alors qu'il etait bien en base.
      */
      propagerLePost(post);
      setPostCherche(true);
      return;
    }
    let vivant = true;
    let essais = 0;
    let differe: number | null = null;
    const chercher = () => {
      void postsApi
        .byId(postId)
        .then((trouve) => {
          if (!vivant) return;
          if (trouve) setPost(trouve);
          else setPostCherche(true);
        })
        .catch(() => {
          if (!vivant) return;
          essais += 1;
          if (essais < 3) differe = window.setTimeout(chercher, 800 * essais);
          else setPostCherche(true);
        });
    };
    chercher();
    return () => {
      vivant = false;
      if (differe !== null) window.clearTimeout(differe);
    };
  }, [postId, post]);

  /*
    Arrive pour une reponse precise (`?c=`) : la cloche ou la notification
    qui l'annoncait. On descend jusqu'a elle — ou jusqu'a la reponse du
    post qui la porte, quand elle est plus bas dans le fil : seules les
    reponses au post sont dans cette liste. Une fois le post en place, et
    pas avant : il pousse tout vers le bas en arrivant. Une lueur breve
    la designe, une seule fois.
  */
  const [adresse] = useSearchParams();
  const vise = adresse.get('c');
  const viseAtteint = useRef(false);
  useEffect(() => {
    if (!vise || viseAtteint.current || !data?.length || (!post && !postCherche)) return;
    const parId = new Map(data.map((commentaire) => [commentaire.id, commentaire]));
    let cible = parId.get(vise);
    const vus = new Set<string>();
    while (cible?.parentId && parId.has(cible.parentId) && !vus.has(cible.id)) {
      vus.add(cible.id);
      cible = parId.get(cible.parentId);
    }
    if (!cible) return;
    viseAtteint.current = true;
    const id = cible.id;
    requestAnimationFrame(() =>
      requestAnimationFrame(() => {
        const ligne = document.querySelector<HTMLElement>(
          `[data-commentaire="${CSS.escape(id)}"]`,
        );
        if (!ligne) return;
        ligne.scrollIntoView({ block: 'center' });
        ligne.classList.add('cm-item--designe');
        window.setTimeout(() => ligne.classList.remove('cm-item--designe'), 1800);
      }),
    );
  }, [vise, data, post, postCherche]);

  const [sending, setSending] = useState(false);
  const [failure, setFailure] = useState<string | null>(null);
  /*
    Le retour d'un ecran de signalement. L'ecran s'etant remonte entre
    temps, ce qui vient d'etre signale a deja disparu du fil.
  */
  useFlashOnArrival(setFailure);
  const composeur = useRef<PoigneeComposeur | null>(null);
  const endRef = useRef<HTMLDivElement>(null);
  const [menu, setMenu] = useState(false);
  /*
    Ce qui est en cours de partage.

    Une reponse se partage comme le post qui la porte : la feuille est la
    meme, seuls changent ce qu'elle envoie et le bouton d'ou elle sort.
  */
  const [partage, setPartage] = useState<{
    postId: string;
    texte?: string;
    /* D'une reponse, donc sortie du menu d'une reponse et non du post. */
    reponse: boolean;
  } | null>(null);
  /*
    Ce que la feuille de partage a fait.

    Distinct de `failure`, qui s'affiche en rouge sous le post et y reste :
    un envoi reussi n'est pas une erreur, et une confirmation n'a rien a
    faire la trois minutes plus tard. Meme pastille ephemere que le feed.
  */
  const [toast, setToast] = useState<string | null>(null);
  /* Les boutons d'ou les deux menus sortent. */
  const menuAnchor = useRef<HTMLElement | null>(null);
  const commentAnchor = useRef<HTMLElement | null>(null);
  /*
    La reponse dont le menu est ouvert. L'etat vit ici, et non dans la
    ligne : une feuille d'actions posee dans la zone defilante s'ouvrirait
    sous le pli, `position: absolute` s'ancrant a cette zone.
  */
  const [menuComment, setMenuComment] = useState<Comment | null>(null);
  const { pending, request: ask, cancel, confirm } = useConfirm();

  /*
    Aucun defilement a l'ouverture, et c'est voulu : l'ecran monte deja en
    haut, face au post. C'est un `scrollIntoView` sur la fin de la liste
    qui l'en delogeait.

    Un fil de discussion s'ouvre sur sa derniere replique — c'est elle
    qu'on vient lire. Un fil de commentaires, non : on vient de toucher un
    post, et c'est lui qu'on s'attend a retrouver sous les yeux.
  */

  /*
    Venu du bouton « commenter » plutot que du corps du post : le champ
    prend le doigt et le clavier monte. On arrive pour ecrire, pas pour
    lire — inutile de demander un appui de plus.
  */
  useEffect(() => {
    if (!(state as { compose?: boolean } | null)?.compose) return;
    composeur.current?.focus();
  }, [state]);

  /* Une seule fonction pour toutes les lignes : elles ne different que
     par le commentaire qu'elles remplacent. */
  function majCommentaire(next: Comment) {
    setData((previous) => previous.map((item) => (item.id === next.id ? next : item)));
  }

  async function onSend(content: string, mentions: PostMention[]) {
    setSending(true);
    setFailure(null);
    try {
      const comment = await postsApi.comment(postId, content, null, mentions);
      // On ajoute a la volee plutot que de relire tout le fil.
      setData((previous) => [...previous, comment]);
      /*
        La seule fois ou l'on descend : voir sa propre reponse arriver. Au
        prochain rendu, une fois qu'elle existe dans la liste.
      */
      requestAnimationFrame(() => endRef.current?.scrollIntoView({ block: 'end' }));
    } catch (cause) {
      setFailure(cause instanceof Error ? cause.message : t.fil.commentaires.envoiImpossible);
    } finally {
      setSending(false);
    }
  }

  /** Like et signet repondent au doigt, puis se confirment. */
  async function onLike() {
    if (!post) return;
    const optimistic = {
      ...post,
      liked: !post.liked,
      likeCount: post.likeCount + (post.liked ? -1 : 1),
    };
    setPost(optimistic);
    try {
      setPost(await postsApi.like(post));
    } catch {
      setPost(post);
    }
  }

  async function onBookmark() {
    if (!post) return;
    setPost({ ...post, bookmarked: !post.bookmarked });
    try {
      setPost(await postsApi.bookmark(post));
    } catch {
      setPost(post);
    }
  }

  /*
    Les memes actions que dans le feed, et rien d'autre : c'est le meme
    post, vu d'un autre ecran. Un post partage depuis ici renvoie vers son
    fil de commentaires plutot que vers le feed — c'est de la qu'on l'a
    ouvert, et c'est la qu'il se lit en entier.
  */
  /**
   * Ouvre la feuille de partage — la meme que dans le feed.
   *
   * On ouvrait ici la feuille du systeme, alors que les trois points du
   * feed ouvrent celle de Sirocco : les amis d'abord, le telephone en
   * dessous. Deux partages differents pour un seul geste sur un seul
   * post, selon qu'on l'avait ouvert ou non.
   *
   * Le lien change avec : la page publique d'un post se lit sans compte,
   * la l'on envoyait l'adresse de son fil de commentaires, qui ne montre
   * rien a qui n'est pas connecte.
   */
  function onShare() {
    if (!post) return;
    setMenu(false);
    setPartage({ postId: post.id, texte: post.content || undefined, reponse: false });
  }

  /** Petit message ephemere, le temps d'etre lu. */
  function flash(message: string) {
    setToast(message);
    setTimeout(() => setToast(null), 2600);
  }

  /** Supprimer son propre post ferme l'ecran : il n'y a plus rien a lire. */
  async function onDelete() {
    if (!post) return;
    setMenu(false);
    try {
      await postsApi.remove(post);
      navigate('/app/feed', { replace: true });
    } catch {
      setFailure(t.fil.commun.suppressionImpossible);
    }
  }

  /**
   * Les memes gestes que sur un post : une reponse en est un.
   *
   * A un ami part le post qui porte la reponse — c'est la seule carte que
   * sait lire une conversation, et de toute facon on ne lit pas une
   * reponse sans ce a quoi elle repond. Le lien, lui, tombe juste : la
   * page publique d'un post ouvre son fil de commentaires.
   */
  function onShareComment(comment: Comment) {
    setMenuComment(null);
    setPartage({ postId: comment.postId, texte: comment.content || undefined, reponse: true });
  }

  async function onDeleteComment(comment: Comment) {
    setMenuComment(null);
    setData((previous) => previous.filter((item) => item.id !== comment.id));
    try {
      await postsApi.removeComment(comment);
    } catch {
      reload();
      setFailure(t.fil.commun.suppressionImpossible);
    }
  }

  const isMine = post?.author.id === user?.id;
  const commentIsMine = menuComment?.author.id === user?.id;

  return (
    <SubScreen
      title={t.fil.commentaires.titre}
      overlay={
        <>
          <PhotoViewer src={viewed} onClose={() => setViewed(null)} />

          <Sheet open={menu} anchor={menuAnchor} onClose={() => setMenu(false)}>
            <SheetAction icon={<PaperPlaneTilt className="sens-x" size={22} />} onClick={onShare}>
              {t.fil.commun.partager}
            </SheetAction>

            {/* Un membre ne signale pas ses propres posts : il les supprime. */}
            {isMine ? (
              <SheetAction
                icon={<Trash size={22} />}
                tone="danger"
                onClick={() => ask(t.fil.commun.supprimerPostQ, () => void onDelete())}
              >
                {t.fil.commun.supprimerPost}
              </SheetAction>
            ) : (
              /* Le motif et le detail se choisissent sur leur propre ecran. */
              post && (
                <SheetAction
                  icon={<Flag size={22} />}
                  onClick={() => navigate(reportPath('post', post.id, { serverId: post.serverId }))}
                >
                  {t.fil.commun.signaler}
                </SheetAction>
              )
            )}
          </Sheet>

          <Sheet
            open={menuComment !== null}
            anchor={commentAnchor}
            onClose={() => setMenuComment(null)}
          >
            {menuComment && (
              <>
                <SheetAction
                  icon={<PaperPlaneTilt className="sens-x" size={22} />}
                  onClick={() => onShareComment(menuComment)}
                >
                  {t.fil.commun.partager}
                </SheetAction>

                {commentIsMine ? (
                  <SheetAction
                    icon={<Trash size={22} />}
                    tone="danger"
                    onClick={() =>
                      ask(t.fil.commun.supprimerReponseQ, () => void onDeleteComment(menuComment))
                    }
                  >
                    {t.fil.commun.supprimerReponse}
                  </SheetAction>
                ) : (
                  <SheetAction
                    icon={<Flag size={22} />}
                    onClick={() =>
                      navigate(
                        reportPath('comment', menuComment.id, { postId: menuComment.postId }),
                      )
                    }
                  >
                    {t.fil.commun.signaler}
                  </SheetAction>
                )}
              </>
            )}
          </Sheet>

          <PartageSheet
            open={partage !== null}
            anchor={partage?.reponse ? commentAnchor : menuAnchor}
            onClose={() => setPartage(null)}
            quoi={partage ? { postId: partage.postId } : null}
            lien={partage ? lienDePost(partage.postId) : ''}
            texte={partage?.texte}
            onFait={flash}
          />

          <ConfirmDialog pending={pending} onCancel={cancel} onConfirm={confirm} />

          {toast && <p className="feed__toast">{toast}</p>}
        </>
      }
      bottomBar={
        <CommentComposer poignee={composeur} onEnvoyer={onSend} envoi={sending} />
      }
    >
      {post && (
        <article className="cm-post">
          {/*
            L'auteur sur sa propre ligne, avatar compris : le texte du post
            prend alors toute la largeur au lieu d'etre repousse dans une
            colonne, comme l'est une reponse.
          */}
          <div className="cm-post__body">
            <Link
              to={profilePath(post.author.id, user?.id)}
              className="cm-post__avatar"
              aria-label={post.author.username}
            >
              <Avatar user={post.author} size={42} />
            </Link>

            <span className="cm-post__head">
              <Link to={profilePath(post.author.id, user?.id)} className="cm-post__author">
                {post.author.username}
              </Link>
            </span>

            <button
              className="cm-post__menu"
              aria-label={t.fil.commun.actionsPost}
              onClick={(pressed) => {
                menuAnchor.current = pressed.currentTarget;
                setMenu(true);
              }}
            >
              <DotsThreeVertical size={22} weight="bold" />
            </button>
          </div>

          {post.content && (
            <PostText
              className="cm-post__content"
              content={post.content}
              mentions={post.mentions}
            />
          )}

          {/*
            La sortie annoncee par le post, ici aussi.

            L'ecran la laissait tomber : un post qui joint un evenement le
            montrait dans le feed et plus du tout des qu'on l'ouvrait,
            alors que c'est l'ecran ou l'on vient pour en savoir plus.
            Rien a arreter au passage — on est deja dans le fil de ce
            post, un appui ne peut mener qu'a la fiche.
          */}
          {post.attachedEventId && <AttachedEvent eventId={post.attachedEventId} />}

          {post.imageUrl && (
            <button
              className="post__photo"
              aria-label={t.fil.commun.voirPhoto}
              onClick={() => setViewed(post.imageUrl)}
            >
              <img src={post.imageUrl} alt={t.fil.commun.photoDe(post.author.username)} />
            </button>
          )}

          {/* Meme barre d'actions que dans le feed, au meme ecart du texte. */}
          {/*
            L'heure et le jour, entre le post et ses actions.

            L'age etait dans l'en-tete — « il y a 1 j » — et c'est ce
            qu'il faut dans une liste, ou l'on parcourt. Ici on s'est
            arrete sur un post : la question devient quand il a ete
            ecrit, et un age n'y repond pas. Meme place et meme forme que
            sous une reponse ouverte, parce que c'est le meme besoin.
          */}
          <p className="cm-post__quand">{horodatage(post.createdAt)}</p>

          <footer className="post__actions">
            <button
              className={`post__action${post.liked ? ' post__action--liked' : ''}`}
              onClick={() => void onLike()}
              aria-label={post.liked ? t.fil.commun.retirerLike : t.fil.commun.liker}
              aria-pressed={post.liked}
            >
              <Heart size={17} weight={post.liked ? 'fill' : 'regular'} />
              {post.likeCount > 0 && <span>{post.likeCount}</span>}
            </button>

            <button
              className={`post__action${post.bookmarked ? ' post__action--saved' : ''}`}
              onClick={() => void onBookmark()}
              aria-label={post.bookmarked ? t.fil.commun.retirerSignet : t.fil.commun.enregistrer}
              aria-pressed={post.bookmarked}
            >
              <BookmarkSimple size={17} weight={post.bookmarked ? 'fill' : 'regular'} />
              {post.bookmarkCount > 0 && <span>{post.bookmarkCount}</span>}
            </button>

            {/* Le lieu, a la meme place que dans le feed. Il disparaissait
                en entrant ici, alors que c'est justement l'ecran ou l'on
                vient pour en savoir plus. */}
            {post.location && (
              <span className="post__place">
                <MapPin size={15} weight="fill" />
                {post.location}
              </span>
            )}
          </footer>
        </article>
      )}

      {loading && <ScreenLoader />}
      {error && <ScreenError message={error} onRetry={reload} />}
      {failure && <p className="field__error">{failure}</p>}

      {data && data.length === 0 && (
        <EmptyState title={t.fil.commentaires.vide} hint={t.fil.commentaires.videAide} />
      )}

      {data && data.length > 0 && (
        <>
          {data.length > 0 && <p className="cm-heading">{t.fil.commun.reponses}</p>}
          {/*
            Seules les reponses de premier rang.

            Une reponse a une reponse appartient au fil de celle-ci, et
            s'y lit : elle n'a pas a remonter ici. Le fil du post reste
            donc une liste de sujets, pas un mur ou l'on cherche a quoi
            repond quoi.
          */}
          <div className="cm-list">
            {racines(data).map((comment) => (
                <CommentRow
                  key={comment.id}
                  comment={comment}
                  onMenu={(trigger) => {
                    commentAnchor.current = trigger;
                    setMenuComment(comment);
                  }}
                onChange={majCommentaire}
              />
            ))}
          </div>
        </>
      )}

      <div ref={endRef} />
    </SubScreen>
  );
}
